import { useCallback, useEffect, useMemo, useState } from 'react';
import { Autocomplete, TextField } from '@mui/material';
import type {
  FieldMappingRule,
  FieldSuggestion,
  NormalizationProfile,
  PriorityMappingConfig,
  TransformType,
} from '../../../types/pipelineConfig';
import {
  TARGET_FIELD_OPTIONS,
  type TargetFieldOption,
} from './normalizedTargetFields';
import { FieldPathInput } from '../FieldPathInput';
import { FieldTagList } from '../FieldTagList';
import { MappingTableEditor } from '../MappingTableEditor';
import {
  PriorityMappingSection,
} from './PriorityMappingSection';

const DEFAULT_PRIORITY_MAPPING_CONFIG: PriorityMappingConfig = {
  mode: 'singleField',
  sourceField: '',
  singleFieldMappings: {},
};

interface NormalizationProfileEditorProps {
  profile: NormalizationProfile;
  onProfileChange: (profile: NormalizationProfile) => void;
  fieldSuggestions?: FieldSuggestion[];
  showDefaults?: boolean;
  tenantId?: string;
  /** When true, priority mapping section is optional (for conditional profiles) */
  isConditionalProfile?: boolean;
}

const MONITOR_CONDITION_OPTIONS = ['Fired', 'Resolved'];

const ENUM_FIELD_OPTIONS: Record<string, string[]> = {
  impact: ['High', 'Medium', 'Low'],
  urgency: ['High', 'Medium', 'Low'],
  monitorCondition: MONITOR_CONDITION_OPTIONS,
};

const TRANSFORM_OPTIONS: Array<{ value: TransformType; label: string }> = [
  { value: 'direct', label: 'Direct Copy' },
  { value: 'mapping', label: 'Value Mapping' },
  { value: 'multiValueMapping', label: 'Multi-value Mapping' },
  { value: 'template', label: 'Template' },
  { value: 'regex', label: 'Regex' },
  { value: 'substring', label: 'Substring' },
  { value: 'split', label: 'Split' },
  { value: 'fallback', label: 'Fallback Fields' },
  { value: 'stringToNumber', label: 'String → Number' },
  { value: 'numberToString', label: 'Number → String' },
  { value: 'script', label: 'Script' },
];

function createEmptyFieldMapping(): FieldMappingRule {
  return {
    sourceField: '',
    targetField: 'title',
    transformType: 'direct',
    isEnabled: true,
  };
}

export function NormalizationProfileEditor({
  profile,
  onProfileChange,
  fieldSuggestions = [],
  showDefaults = true,
  tenantId,
  isConditionalProfile = false,
}: NormalizationProfileEditorProps) {
  const [collapsedRules, setCollapsedRules] = useState<Set<number>>(
    () => new Set(profile.rules.map((_, i) => i)),
  );
  const [defaultsCollapsed, setDefaultsCollapsed] = useState(true);

  const toggleRuleCollapsed = useCallback((index: number) => {
    setCollapsedRules((prev) => {
      const next = new Set(prev);
      if (next.has(index)) next.delete(index);
      else next.add(index);
      return next;
    });
  }, []);

  const updateProfile = useCallback(
    (patch: Partial<NormalizationProfile>) => {
      onProfileChange({
        ...profile,
        ...patch,
      });
    },
    [onProfileChange, profile],
  );

  // Ensure Impact and Urgency defaults are always present for the default profile
  const ensuredDefaults = useMemo(() => {
    if (isConditionalProfile) return profile.defaults ?? {};
    const defaults = { ...(profile.defaults ?? {}) };
    if (!defaults.impact) defaults.impact = 'Medium';
    if (!defaults.urgency) defaults.urgency = 'Medium';
    return defaults;
  }, [profile.defaults, isConditionalProfile]);

  // Sync ensured defaults back to profile if they differ
  const defaultsMissing =
    !isConditionalProfile &&
    (!profile.defaults?.impact || !profile.defaults?.urgency);

  useEffect(() => {
    if (defaultsMissing) {
      updateProfile({ defaults: ensuredDefaults });
    }
    // Only run when defaults are missing, not on every render
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultsMissing]);

  const updateRule = useCallback(
    (index: number, patch: Partial<FieldMappingRule>) => {
      const nextRules = profile.rules.map((rule, ruleIndex) =>
        ruleIndex === index ? { ...rule, ...patch } : rule,
      );
      updateProfile({ rules: nextRules });
    },
    [profile.rules, updateProfile],
  );

  const addRule = useCallback(() => {
    updateProfile({
      rules: [...profile.rules, createEmptyFieldMapping()],
    });
  }, [profile.rules, updateProfile]);

  const removeRule = useCallback(
    (index: number) => {
      updateProfile({
        rules: profile.rules.filter((_, ruleIndex) => ruleIndex !== index),
      });
    },
    [profile.rules, updateProfile],
  );

  return (
    <div className="space-y-4">
      {isConditionalProfile && (
        <div>
          <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">Profile Name</label>
          <input
            type="text"
            className="input"
            value={profile.name}
            onChange={(event) => updateProfile({ name: event.target.value })}
            placeholder="Normalization profile name"
          />
        </div>
      )}

      <div className="space-y-3">
        {profile.rules.length > 0 && (
          <div className="flex justify-end">
            <button
              type="button"
              className="text-xs text-blue-600 dark:text-blue-400 hover:underline"
              onClick={() => {
                const allIndices = profile.rules.map((_, i) => i);
                setCollapsedRules((prev) => (prev.size === allIndices.length ? new Set() : new Set(allIndices)));
              }}
            >
              {collapsedRules.size === profile.rules.length ? 'Expand All' : 'Collapse All'}
            </button>
          </div>
        )}
        {profile.rules.map((rule, index) => {
          const isCollapsed = collapsedRules.has(index);
          return (
            <div key={index}>
              <div
                className="flex items-center gap-2 px-3 py-2 cursor-pointer select-none rounded-t-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800"
                onClick={() => toggleRuleCollapsed(index)}
              >
                <span className="text-xs text-gray-400">{isCollapsed ? '▶' : '▼'}</span>
                <span className="text-xs text-gray-500 dark:text-gray-400">#{index + 1}</span>
                <span className="text-sm text-gray-700 dark:text-gray-300 truncate">
                  <strong>{rule.sourceField || '(no source)'}</strong>
                  <span className="text-gray-400 mx-1">→</span>
                  <strong>{rule.targetField || '(no target)'}</strong>
                  <span className="text-gray-400 mx-1">·</span>
                  <span className="text-gray-500">{rule.transformType}</span>
                </span>
              </div>
              {!isCollapsed && (
                <FieldMappingCard
                  index={index}
                  rule={rule}
                  fieldSuggestions={fieldSuggestions}
                  onChange={updateRule}
                  onRemove={removeRule}
                />
              )}
            </div>
          );
        })}

        <button
          type="button"
          onClick={addRule}
          className="w-full rounded-lg border-2 border-dashed border-gray-300 dark:border-gray-600 px-3 py-2.5 text-sm text-gray-600 dark:text-gray-400 hover:border-primary-500 hover:text-primary-700 dark:hover:text-primary-400"
        >
          + Add Field Mapping
        </button>
      </div>

      <PriorityMappingSection
        config={profile.priorityMappingConfig ?? DEFAULT_PRIORITY_MAPPING_CONFIG}
        onChange={(priorityMappingConfig: PriorityMappingConfig) =>
          updateProfile({ priorityMappingConfig })
        }
        fieldSuggestions={fieldSuggestions}
        tenantId={tenantId}
        isOptional={isConditionalProfile}
      />

      {showDefaults && (
        <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 overflow-hidden">
          <div
            className="flex items-center gap-2 px-3 py-2 cursor-pointer select-none bg-gray-50 dark:bg-gray-800"
            onClick={() => setDefaultsCollapsed((v) => !v)}
          >
            <span className="text-xs text-gray-400">{defaultsCollapsed ? '▶' : '▼'}</span>
            <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Defaults</h4>
            {!isConditionalProfile && (
              <span className="text-xs text-amber-600 dark:text-amber-400">
                Impact &amp; Urgency defaults required
              </span>
            )}
          </div>
          {!defaultsCollapsed && (
            <div className="p-3">
              <MappingTableEditor
                mappings={ensuredDefaults}
                onChange={(defaults) => updateProfile({ defaults })}
                sourceLabel="Field"
                targetLabel="Default Value"
                targetOptionsForKey={(key) => ENUM_FIELD_OPTIONS[key] ?? []}
              />
            </div>
          )}
        </div>
      )}

    </div>
  );
}

function FieldMappingCard({
  index,
  rule,
  fieldSuggestions,
  onChange,
  onRemove,
}: {
  index: number;
  rule: FieldMappingRule;
  fieldSuggestions: FieldSuggestion[];
  onChange: (index: number, patch: Partial<FieldMappingRule>) => void;
  onRemove: (index: number) => void;
}) {
  const update = useCallback(
    (patch: Partial<FieldMappingRule>) => {
      onChange(index, patch);
    },
    [index, onChange],
  );

  const hasMultiValueMapping = useMemo(
    () =>
      rule.transformType === 'multiValueMapping' ||
      (rule.transformType === 'mapping' && Object.keys(rule.multiValueMapping ?? {}).length > 0),
    [rule.multiValueMapping, rule.transformType],
  );

  const multiValueColumns = useMemo(() => {
    if (!rule.multiValueMapping) return [];
    const firstRow = Object.values(rule.multiValueMapping)[0];
    return firstRow ? Object.keys(firstRow) : [];
  }, [rule.multiValueMapping]);

  const selectedTargetFieldOption = useMemo(
    () =>
      TARGET_FIELD_OPTIONS.find((option) => option.value === rule.targetField) ??
      (rule.targetField || null),
    [rule.targetField],
  );

  const setTransform = useCallback(
    (transformType: TransformType) => {
      const cleared: Partial<FieldMappingRule> = {
        transformType,
        template: undefined,
        mappingTable: undefined,
        multiValueMapping: undefined,
        regexPattern: undefined,
        regexReplacement: undefined,
        substringStart: undefined,
        substringLength: undefined,
        splitDelimiter: undefined,
        splitIndex: undefined,
        fallbackFields: undefined,
        numberFormat: undefined,
        scriptLanguage: undefined,
        scriptBody: undefined,
      };
      if (transformType === 'mapping') {
        cleared.mappingTable = {};
      }
      if (transformType === 'multiValueMapping') {
        cleared.multiValueMapping = { '': { impact: '', urgency: '' } };
      }
      update(cleared);
    },
    [update],
  );

  const addMultiValueColumn = useCallback(
    (column: string) => {
      const next: Record<string, Record<string, string>> = {};
      for (const [source, targets] of Object.entries(rule.multiValueMapping ?? {})) {
        next[source] = { ...targets, [column]: '' };
      }
      if (Object.keys(next).length === 0) {
        next[''] = { [column]: '' };
      }
      update({ multiValueMapping: next, transformType: 'multiValueMapping' });
    },
    [rule.multiValueMapping, update],
  );

  const removeMultiValueColumn = useCallback(
    (column: string) => {
      const next: Record<string, Record<string, string>> = {};
      for (const [source, targets] of Object.entries(rule.multiValueMapping ?? {})) {
        const nextTargets = { ...targets };
        delete nextTargets[column];
        next[source] = nextTargets;
      }
      update({ multiValueMapping: next });
    },
    [rule.multiValueMapping, update],
  );

  return (
    <div className={`rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-3 space-y-3 ${!rule.isEnabled ? 'opacity-60' : ''}`}>
      <div className="flex items-start justify-between gap-2">
        <h5 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Rule {index + 1}</h5>
        <div className="flex items-center gap-2">
          <label className="inline-flex items-center gap-1 text-xs text-gray-600 dark:text-gray-300">
            <input
              type="checkbox"
              checked={rule.isEnabled}
              onChange={(event) => update({ isEnabled: event.target.checked })}
              className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
            />
            Enabled
          </label>
          <button
            type="button"
            onClick={() => onRemove(index)}
            className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:text-red-600"
            aria-label={`Remove rule ${index + 1}`}
          >
            ×
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div>
          <label className="mb-1 block text-xs font-medium text-gray-700 dark:text-gray-300">Source Field</label>
          <FieldPathInput
            value={rule.sourceField}
            onChange={(value) => update({ sourceField: value })}
            suggestions={fieldSuggestions}
            placeholder="source field"
          />
        </div>

        <div>
          <label className="mb-1 block text-xs font-medium text-gray-700 dark:text-gray-300">Target Field</label>
          <Autocomplete<TargetFieldOption, false, false, true>
            freeSolo
            options={TARGET_FIELD_OPTIONS}
            value={selectedTargetFieldOption}
            groupBy={(option) => option.group}
            getOptionLabel={(option) => (typeof option === 'string' ? option : option.label)}
            isOptionEqualToValue={(option, value) =>
              typeof value === 'string' ? option.value === value : option.value === value.value
            }
            onChange={(_, value) => {
              if (typeof value === 'string') {
                update({ targetField: value });
                return;
              }
              update({ targetField: value?.value ?? '' });
            }}
            renderGroup={(params) => (
              <li key={params.key}>
                <div className="px-3 py-1 text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                  {params.group}
                </div>
                <ul>{params.children}</ul>
              </li>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                size="small"
                placeholder="Select or type target field"
                slotProps={{
                  input: {
                    ...params.InputProps,
                    className: 'input',
                  },
                }}
              />
            )}
          />
        </div>

        <div>
          <label className="mb-1 block text-xs font-medium text-gray-700 dark:text-gray-300">Transform Type</label>
          <select
            className="input"
            value={rule.transformType}
            onChange={(event) => setTransform(event.target.value as TransformType)}
          >
            {TRANSFORM_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {(rule.transformType === 'mapping' || rule.transformType === 'multiValueMapping') && (
        <div className="space-y-2">
          {hasMultiValueMapping ? (
            <MultiValueMappingEditor
              mapping={rule.multiValueMapping ?? {}}
              targetColumns={multiValueColumns}
              onChange={(multiValueMapping) => update({ multiValueMapping, transformType: 'multiValueMapping' })}
              onAddColumn={addMultiValueColumn}
              onRemoveColumn={removeMultiValueColumn}
            />
          ) : (
            <MappingTableEditor
              mappings={rule.mappingTable ?? {}}
              onChange={(mappingTable) => update({ mappingTable, transformType: 'mapping' })}
              sourceLabel="Source Value"
              targetLabel="Mapped Value"
            />
          )}
        </div>
      )}

      {rule.transformType === 'template' && (
        <input
          type="text"
          className="input"
          value={rule.template ?? ''}
          onChange={(event) => update({ template: event.target.value })}
          placeholder="{{field}}"
        />
      )}

      {rule.transformType === 'regex' && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          <input
            type="text"
            className="input font-mono text-sm"
            value={rule.regexPattern ?? ''}
            onChange={(event) => update({ regexPattern: event.target.value })}
            placeholder="Regex pattern"
          />
          <input
            type="text"
            className="input font-mono text-sm"
            value={rule.regexReplacement ?? ''}
            onChange={(event) => update({ regexReplacement: event.target.value })}
            placeholder="Replacement"
          />
        </div>
      )}

      {rule.transformType === 'substring' && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          <input
            type="number"
            className="input"
            value={rule.substringStart ?? 0}
            onChange={(event) => update({ substringStart: Number(event.target.value) })}
            placeholder="Start index"
          />
          <input
            type="number"
            className="input"
            value={rule.substringLength ?? 0}
            onChange={(event) => update({ substringLength: Number(event.target.value) })}
            placeholder="Length"
          />
        </div>
      )}

      {rule.transformType === 'split' && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          <input
            type="text"
            className="input"
            value={rule.splitDelimiter ?? ''}
            onChange={(event) => update({ splitDelimiter: event.target.value })}
            placeholder="Delimiter"
          />
          <input
            type="number"
            className="input"
            value={rule.splitIndex ?? 0}
            onChange={(event) => update({ splitIndex: Number(event.target.value) })}
            placeholder="Index"
          />
        </div>
      )}

      {rule.transformType === 'fallback' && (
        <FieldTagList
          fields={rule.fallbackFields ?? []}
          onChange={(fallbackFields) => update({ fallbackFields })}
          suggestions={fieldSuggestions}
          placeholder="Add fallback field"
        />
      )}

      {(rule.transformType === 'stringToNumber' || rule.transformType === 'numberToString') && (
        <input
          type="text"
          className="input"
          value={rule.numberFormat ?? ''}
          onChange={(event) => update({ numberFormat: event.target.value })}
          placeholder="Number format (optional)"
        />
      )}

      {rule.transformType === 'script' && (
        <div className="space-y-2">
          <input
            type="text"
            className="input"
            value={rule.scriptLanguage ?? ''}
            onChange={(event) => update({ scriptLanguage: event.target.value })}
            placeholder="Script language (e.g., javascript)"
          />
          <textarea
            className="input min-h-[100px] font-mono text-xs"
            value={rule.scriptBody ?? ''}
            onChange={(event) => update({ scriptBody: event.target.value })}
            placeholder="return input;"
          />
        </div>
      )}
    </div>
  );
}

interface MultiValueMappingEditorProps {
  mapping: Record<string, Record<string, string>>;
  targetColumns: string[];
  onChange: (mapping: Record<string, Record<string, string>>) => void;
  onAddColumn: (column: string) => void;
  onRemoveColumn: (column: string) => void;
}

function getUniqueSourceValue(baseValue: string, mapping: Record<string, Record<string, string>>) {
  if (!(baseValue in mapping)) return baseValue;
  let index = 1;
  let candidate = `source${index}`;
  while (candidate in mapping) {
    index += 1;
    candidate = `source${index}`;
  }
  return candidate;
}

function MultiValueMappingEditor({
  mapping,
  targetColumns,
  onChange,
  onAddColumn,
  onRemoveColumn,
}: MultiValueMappingEditorProps) {
  const rows = useMemo(() => Object.entries(mapping), [mapping]);

  const availableColumns = useMemo(
    () => TARGET_FIELD_OPTIONS.filter((option) => !targetColumns.includes(option.value)),
    [targetColumns],
  );

  const updateCell = useCallback(
    (sourceValue: string, targetField: string, value: string) => {
      const existingRow = mapping[sourceValue] ?? {};
      const nextRow: Record<string, string> = {};
      for (const column of targetColumns) {
        nextRow[column] = column === targetField ? value : (existingRow[column] ?? '');
      }
      onChange({
        ...mapping,
        [sourceValue]: nextRow,
      });
    },
    [mapping, onChange, targetColumns],
  );

  const renameSourceValue = useCallback(
    (oldSourceValue: string, nextSourceValue: string) => {
      const desiredKey = nextSourceValue.trim();
      const finalKey =
        desiredKey !== oldSourceValue && desiredKey in mapping
          ? getUniqueSourceValue(desiredKey, mapping)
          : desiredKey;
      const updated: Record<string, Record<string, string>> = {};
      for (const [source, targets] of Object.entries(mapping)) {
        if (source === oldSourceValue) {
          updated[finalKey] = { ...targets };
        } else {
          updated[source] = { ...targets };
        }
      }
      onChange(updated);
    },
    [mapping, onChange],
  );

  const removeSourceValue = useCallback(
    (sourceValue: string) => {
      const updated: Record<string, Record<string, string>> = {};
      for (const [source, targets] of Object.entries(mapping)) {
        if (source !== sourceValue) {
          updated[source] = { ...targets };
        }
      }
      onChange(updated);
    },
    [mapping, onChange],
  );

  const addRow = useCallback(() => {
    const row: Record<string, string> = {};
    for (const column of targetColumns) {
      row[column] = '';
    }
    const sourceValue = getUniqueSourceValue('', mapping);
    onChange({
      ...mapping,
      [sourceValue]: row,
    });
  }, [mapping, onChange, targetColumns]);

  return (
    <div className="space-y-2">
      <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
        <table className="min-w-full text-sm">
          <thead>
            <tr className="bg-gray-50 dark:bg-gray-800/60">
              <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wider text-gray-600 dark:text-gray-300">
                Source Value
              </th>
              {targetColumns.map((targetField) => {
                const option = TARGET_FIELD_OPTIONS.find((o) => o.value === targetField);
                return (
                  <th key={targetField} className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wider text-gray-600 dark:text-gray-300">
                    <div className="flex items-center gap-1">
                      <span>{option?.label ?? targetField}</span>
                      {targetColumns.length > 1 && (
                        <button
                          type="button"
                          onClick={() => onRemoveColumn(targetField)}
                          className="text-gray-400 hover:text-red-500 text-xs leading-none"
                        >
                          ×
                        </button>
                      )}
                    </div>
                  </th>
                );
              })}
              <th className="px-3 py-2 w-10" />
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
            {rows.map(([sourceValue, targets], rowIndex) => (
              <tr key={rowIndex} className={rowIndex % 2 === 0 ? 'bg-white dark:bg-gray-900' : 'bg-gray-50/50 dark:bg-gray-800/40'}>
                <td className="px-3 py-2 align-top min-w-[180px]">
                  <input
                    type="text"
                    className="input"
                    value={sourceValue}
                    onChange={(event) => renameSourceValue(sourceValue, event.target.value)}
                    placeholder="source value"
                  />
                </td>
                {targetColumns.map((targetField) => (
                  <td key={`${sourceValue}-${targetField}`} className="px-3 py-2 align-top min-w-[160px]">
                    <input
                      type="text"
                      className="input"
                      value={targets[targetField] ?? ''}
                      onChange={(event) => updateCell(sourceValue, targetField, event.target.value)}
                      placeholder={targetField}
                    />
                  </td>
                ))}
                <td className="px-3 py-2 align-top">
                  <button
                    type="button"
                    onClick={() => removeSourceValue(sourceValue)}
                    className="text-gray-400 hover:text-red-600 dark:hover:text-red-400"
                  >
                    ×
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          onClick={addRow}
          className="rounded-md border border-gray-300 dark:border-gray-600 px-3 py-1.5 text-xs text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800"
        >
          Add Row
        </button>
        {availableColumns.length > 0 && (
          <select
            className="rounded-md border border-gray-300 dark:border-gray-600 px-3 py-1.5 text-xs text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-900 hover:bg-gray-100 dark:hover:bg-gray-800"
            value=""
            onChange={(event) => {
              if (event.target.value) onAddColumn(event.target.value);
            }}
          >
            <option value="">+ Add Target Column...</option>
            {Object.entries(
              availableColumns.reduce<Record<string, TargetFieldOption[]>>((acc, option) => {
                (acc[option.group] ??= []).push(option);
                return acc;
              }, {}),
            ).map(([group, options]) => (
              <optgroup key={group} label={group}>
                {options.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </optgroup>
            ))}
          </select>
        )}
      </div>
    </div>
  );
}

export type { NormalizationProfileEditorProps };
